(function () {
  const STORAGE_KEY = 'vio-selected-product-handle';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function deepMerge(base, override) {
    if (!override || typeof override !== 'object') return base;
    const result = Array.isArray(base) ? [...base] : { ...base };
    Object.keys(override).forEach((key) => {
      const val = override[key];
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        result[key] = deepMerge(result[key] || {}, val);
      } else if (val !== undefined && val !== null) {
        result[key] = val;
      }
    });
    return result;
  }

  const DEFAULT_ENABLED_SECTIONS = {
    story: true,
    hud: true,
    features: true,
    'how-to': true,
    specs: true,
    faq: true,
  };

  const ZONE_UPDATERS = {
    story: updateStoryZone,
    hud: updateHudZone,
    features: updateFeaturesZone,
    'how-to': updateHowToZone,
    specs: updateSpecsZone,
    faq: updateFaqZone,
  };

  function resolveEnabledSections(entry) {
    return { ...DEFAULT_ENABLED_SECTIONS, ...(entry.enabledSections || {}) };
  }

  function isZoneEnabled(product, zoneKey) {
    const sections = product.enabledSections || DEFAULT_ENABLED_SECTIONS;
    return sections[zoneKey] !== false;
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function buildProductPayload(entry, defaults) {
    let homepage = entry.homepage;
    if (typeof homepage === 'string') {
      try {
        homepage = JSON.parse(homepage);
      } catch (error) {
        homepage = null;
      }
    }

    const merged = deepMerge(JSON.parse(JSON.stringify(defaults)), homepage || {});
    const desc = entry.description || '';

    if (entry.featuredImage) {
      merged.story = merged.story || {};
      merged.story.deviceImage = entry.featuredImage;
    }

    if (desc && merged.story && merged.story.chapters && merged.story.chapters[0]) {
      if (!entry.homepage || !entry.homepage.story) {
        merged.story.chapters[0].body = desc.slice(0, 280);
      }
    }

    return {
      handle: entry.handle,
      id: entry.id,
      title: entry.title,
      url: entry.url,
      price: entry.price,
      unitPrice: entry.unitPrice,
      variantId: entry.variantId,
      quantityMin: entry.quantityMin ?? 1,
      quantityMax: entry.quantityMax ?? null,
      quantityStep: entry.quantityStep ?? 1,
      featuredImage: entry.featuredImage,
      description: entry.description,
      homepageStoryOverride: Boolean(homepage && homepage.story),
      story: merged.story || {},
      hud: merged.hud || [],
      features: merged.features || [],
      howTo: merged.howTo || [],
      specs: merged.specs || { rows: [] },
      faq: merged.faq || [],
      faqItems: Array.isArray(entry.faqItems) ? entry.faqItems : [],
      specItems: Array.isArray(entry.specItems) ? entry.specItems : [],
      specDimensionImage: entry.specDimensionImage || '',
      hudItems: Array.isArray(entry.hudItems) ? entry.hudItems : [],
      featureItems: Array.isArray(entry.featureItems) ? entry.featureItems : [],
      howToItems: Array.isArray(entry.howToItems) ? entry.howToItems : [],
      enabledSections: resolveEnabledSections(entry),
      demo: Boolean(entry.demo),
    };
  }

  const VioProductContext = {
    defaults: null,
    products: [],
    catalog: {},
    payloads: {},
    selectedHandle: null,

    init(config) {
      this.defaults = config.defaults;
      this.products = config.products || [];
      this.catalog = {};
      this.payloads = {};
      this.products.forEach((entry) => {
        this.catalog[entry.handle] = entry;
        this.payloads[entry.handle] = buildProductPayload(entry, this.defaults);
      });

      const hashMatch = window.location.hash.match(/product=([^&]+)/);
      const stored = sessionStorage.getItem(STORAGE_KEY);
      const initial =
        (hashMatch && this.payloads[hashMatch[1]] && hashMatch[1]) ||
        (stored && this.payloads[stored] && stored) ||
        config.defaultHandle ||
        (this.products[0] && this.products[0].handle);

      if (initial) this.select(initial, { persist: false, scrollCard: false });
    },

    getSelected() {
      return this.payloads[this.selectedHandle] || null;
    },

    select(handle, options = {}) {
      if (!this.payloads[handle]) return;
      this.selectedHandle = handle;
      if (options.persist !== false) {
        sessionStorage.setItem(STORAGE_KEY, handle);
      }
      applyProductContent(this.payloads[handle]);
      document.dispatchEvent(
        new CustomEvent('vio:product-change', {
          detail: { handle, product: this.payloads[handle] },
        })
      );
      document.querySelectorAll('vio-product-picker').forEach((picker) => {
        picker.syncSelection(handle);
      });
    },
  };

  function setZoneUpdating(updating) {
    document.querySelectorAll('[data-vio-product-zone]').forEach((zone) => {
      zone.classList.toggle('vio-product-zone--updating', updating);
    });
  }

  function applyZoneVisibility(product) {
    document.querySelectorAll('[data-vio-product-zone]').forEach((zone) => {
      const zoneKey = zone.dataset.vioProductZone;
      const enabled = isZoneEnabled(product, zoneKey);

      zone.classList.toggle('vio-product-zone--hidden', !enabled);
      if (enabled) {
        zone.removeAttribute('hidden');
      } else {
        zone.setAttribute('hidden', '');
      }
    });
  }

  function applyProductContent(product) {
    setZoneUpdating(true);

    window.requestAnimationFrame(() => {
      Object.entries(ZONE_UPDATERS).forEach(([, updater]) => {
        updater(product);
      });
      applyZoneVisibility(product);
      setZoneUpdating(false);
      window.VioSectionRail?.refresh?.();
      window.VioProductDock?.refresh?.();
    });
  }

  function updateStoryZone(product) {
    const zone = document.querySelector('[data-vio-product-zone="story"]');
    if (!zone) return;

    const story = product.story || {};
    const chapters = story.chapters || [];
    const hasHomepageStory = Boolean(product.homepageStoryOverride);

    zone.querySelectorAll('[data-vio-bind^="story.chapters."]').forEach((el) => {
      const bind = el.dataset.vioBind;
      const match = bind && bind.match(/^story\.chapters\.(\d+)\.(heading|body)$/);
      if (!match) return;

      const index = Number(match[1]);
      const field = match[2];
      const chapter = chapters[index];
      const sectionKey = field === 'heading' ? 'sectionHeading' : 'sectionBody';
      const sectionText = el.dataset[sectionKey];

      if (hasHomepageStory && chapter && chapter[field]) {
        el.textContent = chapter[field];
      } else if (sectionText !== undefined) {
        el.textContent = sectionText;
      }
    });

    chapters.forEach((chapter, index) => {
      swapStoryChapterImage(zone, index, chapter);
    });

    const outro = document.querySelector('[data-vio-bind="story.outro"]');
    if (outro) {
      if (hasHomepageStory && story.outro) {
        outro.textContent = story.outro;
      } else if (outro.dataset.sectionOutro !== undefined) {
        outro.textContent = outro.dataset.sectionOutro;
      }
    }

    const checklist = zone.querySelector('[data-vio-bind="story.checklist"]');
    if (checklist && hasHomepageStory && story.checklist?.length) {
      checklist.innerHTML = story.checklist
        .map((item) => `<li class="vio-story-checklist__item vio-hud-chip">${escapeHtml(item)}</li>`)
        .join('');
    }

    swapStoryDeviceImage(zone, product.featuredImage || story.deviceImage);
  }

  function swapStoryChapterImage(zone, index, chapter) {
    const container = zone.querySelector(`[data-vio-bind="story.chapters.${index}.image"]`);
    if (!container) return;

    const imageUrl = (chapter && chapter.image) || container.dataset.sectionImage || '';
    let img = container.querySelector('.vio-story-media__image');

    if (!img) {
      img = document.createElement('img');
      img.className = 'vio-story-media__image';
      img.loading = 'lazy';
      img.alt = '';
      const frame = container.querySelector('.vio-story-media__frame');
      const placeholder = container.querySelector('.vio-story-media__placeholder');
      if (frame && placeholder) {
        frame.insertBefore(img, placeholder);
      } else if (frame) {
        frame.appendChild(img);
      }
    }

    if (imageUrl) {
      img.src = imageUrl;
      img.alt = chapter?.heading || VioProductContext.getSelected()?.title || '';
      img.hidden = false;
      container.classList.add('vio-story-media--has-image');
    } else {
      img.removeAttribute('src');
      img.hidden = true;
      container.classList.remove('vio-story-media--has-image');
    }
  }

  function swapStoryDeviceImage(zone, imageUrl) {
    const solid = zone.querySelector('.vio-device-media__solid');
    if (!solid) return;
    let img = solid.querySelector('.vio-device-media__image');
    if (!imageUrl) {
      if (img) img.remove();
      return;
    }
    if (!img) {
      img = document.createElement('img');
      img.className = 'vio-device-media__image';
      img.loading = 'lazy';
      solid.appendChild(img);
    }
    img.src = imageUrl;
    img.alt = VioProductContext.getSelected()?.title || '';
  }

  function renderHudSpec(spec, metafieldRow) {
    const bar = spec.showBar
      ? '<div class="vio-device-hud__bar" aria-hidden="true"><div class="vio-device-hud__bar-fill"></div></div>'
      : '';
    const metafieldAttr = metafieldRow ? ' data-vio-hud-metafield-spec' : '';
    const chapter = escapeHtml(spec.chapterLink || 'all');
    return `<div class="vio-device-hud__spec" data-vio-hud-spec data-hud-chapter="${chapter}"${metafieldAttr}>
      <dt class="vio-device-hud__label vio-hud-label">${escapeHtml(spec.label)}</dt>
      <dd class="vio-device-hud__value">${escapeHtml(spec.value)}</dd>
      ${bar}
    </div>`;
  }

  function isProductHudMetafieldEnabled(zone) {
    return zone?.dataset.vioHudMetafieldEnabled !== 'false';
  }

  function updateHudZone(product) {
    const zone = document.querySelector('[data-vio-product-zone="hud"]');
    if (!zone) return;

    const desktop = zone.querySelector('[data-vio-bind="hud.grid"]');
    const mobile = zone.querySelector('[data-vio-bind="hud.gridMobile"]');
    const metafieldHtml = isProductHudMetafieldEnabled(zone)
      ? (product.hudItems || []).map((spec) => renderHudSpec(spec, true)).join('')
      : '';

    [desktop, mobile].forEach((grid) => {
      if (!grid) return;
      grid.querySelectorAll('[data-vio-hud-metafield-spec]').forEach((el) => el.remove());
      if (metafieldHtml) grid.insertAdjacentHTML('beforeend', metafieldHtml);
    });
  }

  function renderFeatureCard(feature) {
    return `<article class="vio-features__card" data-vio-feature-metafield-card>
          ${feature.tag ? `<span class="vio-features__tag vio-hud-chip">${escapeHtml(feature.tag)}</span>` : ''}
          ${feature.title ? `<h3 class="vio-features__title">${escapeHtml(feature.title)}</h3>` : ''}
          ${feature.body ? `<p class="vio-features__body">${escapeHtml(feature.body)}</p>` : ''}
        </article>`;
  }

  function isProductFeaturesMetafieldEnabled(zone) {
    return zone?.dataset.vioFeaturesMetafieldEnabled !== 'false';
  }

  function updateFeaturesZone(product) {
    const zone = document.querySelector('[data-vio-product-zone="features"]');
    const grid = zone?.querySelector('[data-vio-bind="features.grid"]');
    if (!grid) return;

    grid.querySelectorAll('[data-vio-feature-metafield-card]').forEach((el) => el.remove());
    if (isProductFeaturesMetafieldEnabled(zone)) {
      const metafieldHtml = (product.featureItems || []).map((f) => renderFeatureCard(f)).join('');
      if (metafieldHtml) grid.insertAdjacentHTML('beforeend', metafieldHtml);
    }
  }

  function renderHowToStep(step, stepNumber) {
    const num = String(stepNumber).padStart(2, '0');
    return `<li class="vio-how-to__step" data-vio-how-to-metafield-step>
          <span class="vio-how-to__number vio-hud-label">${num}</span>
          <div class="vio-how-to__content">
            ${step.title ? `<h3 class="vio-how-to__title">${escapeHtml(step.title)}</h3>` : ''}
            ${step.body ? `<p class="vio-how-to__body">${escapeHtml(step.body)}</p>` : ''}
          </div>
        </li>`;
  }

  function isProductHowToMetafieldEnabled(zone) {
    return zone?.dataset.vioHowToMetafieldEnabled !== 'false';
  }

  function updateHowToZone(product) {
    const zone = document.querySelector('[data-vio-product-zone="how-to"]');
    const list = zone?.querySelector('[data-vio-bind="howTo.steps"]');
    if (!list) return;

    list.querySelectorAll('[data-vio-how-to-metafield-step]').forEach((el) => el.remove());
    if (!isProductHowToMetafieldEnabled(zone)) return;

    const manualCount = list.querySelectorAll(
      '.vio-how-to__step:not([data-vio-how-to-metafield-step])'
    ).length;
    const metafieldHtml = (product.howToItems || [])
      .map((step, i) => renderHowToStep(step, manualCount + i + 1))
      .join('');
    if (metafieldHtml) list.insertAdjacentHTML('beforeend', metafieldHtml);
  }

  function renderSpecsRow(row) {
    return `<div class="vio-specs__row" data-vio-specs-metafield-row>
            <dt class="vio-specs__label vio-hud-label">${escapeHtml(row.label)}</dt>
            <dd class="vio-specs__value">${escapeHtml(row.value)}</dd>
          </div>`;
  }

  function isProductSpecsMetafieldEnabled(zone) {
    return zone?.dataset.vioSpecsMetafieldEnabled !== 'false';
  }

  function isProductSpecsDimensionMetafieldEnabled(zone) {
    return zone?.dataset.vioSpecsDimensionMetafieldEnabled !== 'false';
  }

  function updateSpecsZone(product) {
    const zone = document.querySelector('[data-vio-product-zone="specs"]');
    if (!zone) return;

    const media = zone.querySelector('[data-vio-bind="specs.media"]');
    if (media) {
      const sectionImage = media.dataset.vioSpecsSectionImage || '';
      const productDiagram = isProductSpecsDimensionMetafieldEnabled(zone)
        ? product.specDimensionImage || ''
        : '';
      const imageUrl = sectionImage || productDiagram;

      if (imageUrl) {
        media.innerHTML = `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(product.title)}" class="vio-specs__image" loading="lazy" width="800" height="800">`;
      } else {
        const mediaFallback = zone.querySelector('[data-vio-specs-media-fallback]');
        media.innerHTML = mediaFallback ? mediaFallback.innerHTML : '';
      }
    }

    const grid = zone.querySelector('[data-vio-bind="specs.rows"]');
    if (grid) {
      grid.querySelectorAll('[data-vio-specs-metafield-row]').forEach((row) => row.remove());
      if (isProductSpecsMetafieldEnabled(zone)) {
        const metafieldHtml = (product.specItems || []).map((row) => renderSpecsRow(row)).join('');
        if (metafieldHtml) grid.insertAdjacentHTML('beforeend', metafieldHtml);
      }
    }
  }

  function renderFaqItem(item) {
    return `<details class="vio-faq__item">
          <summary class="vio-faq__question">
            <span class="vio-faq__question-text">${escapeHtml(item.question)}</span>
            <span class="vio-faq__icon" aria-hidden="true"></span>
          </summary>
          <div class="vio-faq__answer"><p>${escapeHtml(item.answer)}</p></div>
        </details>`;
  }

  function isProductFaqMetafieldEnabled(zone) {
    return zone?.dataset.vioFaqMetafieldEnabled !== 'false';
  }

  function updateFaqZone(product) {
    const zone = document.querySelector('[data-vio-product-zone="faq"]');
    const metafieldItems = zone?.querySelector('[data-vio-faq-metafield-items]');
    if (!metafieldItems) return;

    metafieldItems.innerHTML = isProductFaqMetafieldEnabled(zone)
      ? (product.faqItems || []).map((item) => renderFaqItem(item)).join('')
      : '';
  }

  function getProductBuyUrl(entry) {
    if (!entry?.url || entry.demo || String(entry.url).startsWith('#')) return null;

    try {
      const url = new URL(entry.url, window.location.origin);
      if (entry.variantId) {
        url.searchParams.set('variant', entry.variantId);
      }
      return url.toString();
    } catch (error) {
      return entry.url;
    }
  }

  function parsePickerConfig(pickerEl) {
    const payloadScript = pickerEl.querySelector('[data-vio-product-payloads]');
    if (!payloadScript) return null;

    try {
      return JSON.parse(payloadScript.textContent);
    } catch (error) {
      console.error('Vio product picker: invalid payload JSON', error);
      return null;
    }
  }

  function bindPicker(pickerEl) {
    if (pickerEl.dataset.vioPickerReady === 'true') return;

    const config = parsePickerConfig(pickerEl);
    if (!config) return;

    pickerEl.dataset.vioPickerReady = 'true';
    VioProductContext.init(config);

    if (typeof pickerEl.bindInteractions === 'function') {
      pickerEl.bindInteractions();
      pickerEl.updateNavState();
      if (VioProductContext.selectedHandle) {
        pickerEl.syncSelection(VioProductContext.selectedHandle);
      }
    }
  }

  function bootPickers(root = document) {
    root.querySelectorAll('vio-product-picker').forEach((pickerEl) => {
      bindPicker(pickerEl);
    });
  }

  function clearCardMediaAutoplay(shell) {
    if (shell._vioAutoplayTimer) {
      clearInterval(shell._vioAutoplayTimer);
      shell._vioAutoplayTimer = null;
    }
  }

  function startCardMediaAutoplay(shell) {
    if (reducedMotion) return;

    const items = shell.querySelectorAll('[data-vio-card-media-item]');
    if (items.length < 2) return;

    clearCardMediaAutoplay(shell);

    const intervalMs = Number(shell.dataset.vioMediaAutoplay || 3000);
    shell._vioAutoplayTimer = setInterval(() => {
      if (shell.dataset.vioMediaHovering === 'true') return;

      const current = Number(shell.dataset.vioMediaIndex || 0);
      setCardMediaIndex(shell, current + 1);
    }, intervalMs);
  }

  function resetCardMediaAutoplay(shell) {
    clearCardMediaAutoplay(shell);
    startCardMediaAutoplay(shell);
  }

  function setCardMediaIndex(shell, index, options = {}) {
    const { preview = false } = options;
    const items = [...shell.querySelectorAll('[data-vio-card-media-item]')];
    const thumbs = [...shell.querySelectorAll('[data-vio-media-thumb]')];
    if (!items.length) return;

    const count = items.length;
    const displayIndex = ((index % count) + count) % count;

    items.forEach((img, i) => {
      img.classList.toggle('vio-product-picker__card-image--active', i === displayIndex);
    });

    if (!preview) {
      shell.dataset.vioMediaIndex = String(displayIndex);

      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('vio-product-picker__media-thumb--active', i === displayIndex);
        thumb.setAttribute('aria-selected', i === displayIndex ? 'true' : 'false');
        thumb.setAttribute('aria-label', `Image ${i + 1} of ${count}`);
      });
    }

    const label = `Image ${displayIndex + 1} of ${count}`;
    const prevBtn = shell.querySelector('[data-vio-media-prev]');
    const nextBtn = shell.querySelector('[data-vio-media-next]');
    if (prevBtn) prevBtn.setAttribute('aria-label', `Previous image (${label})`);
    if (nextBtn) nextBtn.setAttribute('aria-label', `Next image (${label})`);
  }

  function initCardMediaGallery(shell) {
    if (shell.dataset.vioMediaGalleryReady === 'true') return;

    const items = shell.querySelectorAll('[data-vio-card-media-item]');
    if (items.length < 2) {
      if (items.length === 1) {
        items[0].classList.add('vio-product-picker__card-image--active');
      }
      return;
    }

    shell.dataset.vioMediaGalleryReady = 'true';
    shell.dataset.vioMediaIndex = '0';
    shell.dataset.vioMediaHovering = 'false';
    setCardMediaIndex(shell, 0);

    shell.querySelector('[data-vio-media-prev]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      const current = Number(shell.dataset.vioMediaIndex || 0);
      setCardMediaIndex(shell, current - 1);
      resetCardMediaAutoplay(shell);
    });

    shell.querySelector('[data-vio-media-next]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      const current = Number(shell.dataset.vioMediaIndex || 0);
      setCardMediaIndex(shell, current + 1);
      resetCardMediaAutoplay(shell);
    });

    shell.querySelectorAll('[data-vio-media-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', (event) => {
        event.stopPropagation();
        setCardMediaIndex(shell, Number(thumb.dataset.vioMediaThumb));
        resetCardMediaAutoplay(shell);
      });
    });

    const mediaEl = shell.querySelector('[data-vio-card-media]');
    if (mediaEl) {
      mediaEl.addEventListener('mouseenter', () => {
        shell.dataset.vioMediaHovering = 'true';
        clearCardMediaAutoplay(shell);
        const current = Number(shell.dataset.vioMediaIndex || 0);
        setCardMediaIndex(shell, current + 1, { preview: true });
      });

      mediaEl.addEventListener('mouseleave', () => {
        shell.dataset.vioMediaHovering = 'false';
        const current = Number(shell.dataset.vioMediaIndex || 0);
        setCardMediaIndex(shell, current);
        startCardMediaAutoplay(shell);
      });
    }

    startCardMediaAutoplay(shell);
  }

  function destroyCardMediaGallery(shell) {
    clearCardMediaAutoplay(shell);
    delete shell.dataset.vioMediaGalleryReady;
    delete shell.dataset.vioMediaIndex;
    delete shell.dataset.vioMediaHovering;
  }

  function initCardMediaGalleries(root) {
    root.querySelectorAll('.vio-product-picker__card-media-shell').forEach((shell) => {
      initCardMediaGallery(shell);
    });
  }

  function resetCardMediaGalleryForHandle(root, handle) {
    if (!handle) return;
    root.querySelectorAll('.vio-product-picker__card[data-product-handle]').forEach((card) => {
      if (card.dataset.productHandle !== handle) return;
      const shell = card.querySelector('.vio-product-picker__card-media-shell');
      if (shell) {
        setCardMediaIndex(shell, 0);
        resetCardMediaAutoplay(shell);
      }
    });
  }

  class VioProductPicker extends HTMLElement {
    connectedCallback() {
      bindPicker(this);
    }

    disconnectedCallback() {
      delete this.dataset.vioPickerReady;
    }

    bindInteractions() {
      if (this.dataset.vioInteractionsBound === 'true') return;
      this.dataset.vioInteractionsBound = 'true';

      this.isFeatured = this.classList.contains('vio-product-picker--featured');
      this.track = this.querySelector('.vio-product-picker__track');
      this.prevBtn = this.querySelector('[name="previous"]');
      this.nextBtn = this.querySelector('[name="next"]');

      if (!this.track) return;

      initCardMediaGalleries(this);

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-vio-media-prev], [data-vio-media-next], [data-vio-media-thumb]')) {
          return;
        }

        const orderEl = event.target.closest('[data-vio-order]');
        if (orderEl && this.contains(orderEl)) {
          const card = orderEl.closest('.vio-product-picker__card[data-product-handle]');
          if (!card) return;

          const handle = card.dataset.productHandle;
          const entry = VioProductContext.catalog[handle];
          if (entry) {
            VioProductContext.select(handle, { scrollCard: false });
          }

          if (orderEl.tagName === 'A') return;

          event.preventDefault();
          const buyUrl = getProductBuyUrl(entry);
          if (buyUrl) window.location.href = buyUrl;
          return;
        }

        if (this.isFeatured) return;

        const card = event.target.closest('.vio-product-picker__card[data-product-handle]');
        if (!card || !this.contains(card)) return;

        const handle = card.dataset.productHandle;
        if (!VioProductContext.payloads[handle]) {
          console.warn('Vio product picker: no payload for handle', handle);
          return;
        }

        VioProductContext.select(handle);
        card.scrollIntoView({
          behavior: reducedMotion ? 'auto' : 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      });

      if (!this.isFeatured) {
        this.prevBtn?.addEventListener('click', () => this.scrollByPage(-1));
        this.nextBtn?.addEventListener('click', () => this.scrollByPage(1));
        this.track.addEventListener('scroll', () => this.updateNavState(), { passive: true });
        this.track.addEventListener('keydown', (e) => this.onKeydown(e));
      }
    }

    scrollByPage(direction) {
      const slide = this.track?.querySelector('.vio-product-picker__slide');
      if (!slide || !this.track) return;
      const offset = slide.offsetWidth + 16;
      this.track.scrollBy({ left: direction * offset, behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    updateNavState() {
      if (this.isFeatured || !this.track) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 4;
      if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= maxScroll - 4;
    }

    syncSelection(handle) {
      if (this.isFeatured) {
        resetCardMediaGalleryForHandle(this, handle);
        return;
      }

      this.querySelectorAll('.vio-product-picker__card').forEach((card) => {
        const selected = card.dataset.productHandle === handle;
        card.classList.toggle('vio-product-picker__card--selected', selected);
        const selectBtn = card.querySelector('.vio-product-picker__select');
        if (selectBtn) selectBtn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      resetCardMediaGalleryForHandle(this, handle);
    }

    onKeydown(event) {
      const cards = [...this.querySelectorAll('.vio-product-picker__card')];
      const index = cards.findIndex((c) => c.classList.contains('vio-product-picker__card--selected'));
      if (event.key === 'ArrowRight' && index < cards.length - 1) {
        event.preventDefault();
        cards[index + 1].click();
      }
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault();
        cards[index - 1].click();
      }
    }
  }

  if (!customElements.get('vio-product-picker')) {
    customElements.define('vio-product-picker', VioProductPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootPickers());
  } else {
    bootPickers();
  }

  document.addEventListener('shopify:section:load', (event) => {
    bootPickers(event.target);

    const selected = VioProductContext.getSelected();
    if (selected && event.target.querySelector('[data-vio-product-zone="story"]')) {
      updateStoryZone(selected);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('.vio-product-picker__card-media-shell').forEach((shell) => {
      destroyCardMediaGallery(shell);
    });
  });

  VioProductContext.getBuyUrl = getProductBuyUrl;
  window.VioProductContext = VioProductContext;
})();
